import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { supabase, supabaseConfigured } from "../lib/supabase";
import { adaptIdentity, type AppIdentity } from "../lib/adapters";
import { translatePasswordError } from "../lib/password-change";
import type { Database } from "../lib/database.types";

export interface AuthUser {
  id: string;
  email: string | null;
}

export interface SignInResult {
  ok: boolean;
  error?: string;
}

interface AuthState {
  configured: boolean;
  loading: boolean;
  session: Session | null;
  user: AuthUser | null;
  identity: AppIdentity | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<SignInResult>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function translateAuthError(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes("invalid login credentials")) return "E-mail ou senha inválidos.";
  if (normalized.includes("email not confirmed")) return "E-mail ainda não confirmado.";
  if (normalized.includes("too many requests") || normalized.includes("rate limit")) {
    return "Muitas tentativas. Tente novamente em instantes.";
  }
  if (normalized.includes("failed to fetch") || normalized.includes("network")) {
    return "Falha de conexão com o servidor.";
  }
  return "Não foi possível entrar. Verifique os dados e tente novamente.";
}

async function loadIdentity(
  client: SupabaseClient<Database>,
  userId: string,
  email: string | null
): Promise<AppIdentity | null> {
  await client.rpc("bootstrap_user", {});

  const { data: membership, error: membershipError } = await client
    .from("workspace_members")
    .select("workspace_id, role")
    .eq("user_id", userId)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (membershipError || !membership) {
    return null;
  }

  const { data: profile } = await client
    .from("profiles")
    .select("display_name")
    .eq("id", userId)
    .maybeSingle();

  const { data: workspace } = await client
    .from("workspaces")
    .select("name")
    .eq("id", membership.workspace_id)
    .maybeSingle();

  const { data: preferences } = await client
    .from("workspace_preferences")
    .select("timezone")
    .eq("workspace_id", membership.workspace_id)
    .maybeSingle();

  return adaptIdentity({
    userId,
    email,
    profile: profile ?? null,
    membership,
    workspace: workspace ?? null,
    preferences: preferences ?? null,
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = supabase;
  const [loading, setLoading] = useState(supabaseConfigured);
  const [session, setSession] = useState<Session | null>(null);
  const [identity, setIdentity] = useState<AppIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(true);

  const applySession = useCallback(
    async (next: Session | null) => {
      if (!activeRef.current) return;
      setSession(next);
      if (!client || !next?.user) {
        setIdentity(null);
        return;
      }
      const loaded = await loadIdentity(client, next.user.id, next.user.email ?? null);
      if (activeRef.current) {
        setIdentity(loaded);
      }
    },
    [client]
  );

  useEffect(() => {
    activeRef.current = true;

    if (!client) {
      setLoading(false);
      return () => {
        activeRef.current = false;
      };
    }

    client.auth
      .getSession()
      .then(({ data }) => applySession(data.session))
      .finally(() => {
        if (activeRef.current) setLoading(false);
      });

    const { data } = client.auth.onAuthStateChange((_event, nextSession) => {
      void applySession(nextSession);
    });

    return () => {
      activeRef.current = false;
      data.subscription.unsubscribe();
    };
  }, [client, applySession]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      if (!client) {
        return { ok: false, error: "Supabase não configurado neste ambiente." };
      }
      setError(null);
      const { error: signInError } = await client.auth.signInWithPassword({ email, password });
      if (signInError) {
        const message = translateAuthError(signInError.message);
        setError(message);
        return { ok: false, error: message };
      }
      return { ok: true };
    },
    [client]
  );

  const signOut = useCallback(async () => {
    if (!client) return;
    await client.auth.signOut();
    setSession(null);
    setIdentity(null);
  }, [client]);

  /**
   * Troca de senha da própria conta. Confirma a senha atual antes de aplicar
   * (evita troca a partir de uma sessão deixada aberta) e usa apenas a chave
   * publicável — `service_role` nunca chega ao navegador.
   */
  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<SignInResult> => {
      if (!client) {
        return { ok: false, error: "Supabase não configurado neste ambiente." };
      }
      const email = session?.user?.email ?? null;
      if (!email) {
        return { ok: false, error: "Sessão sem e-mail. Entre novamente para trocar a senha." };
      }
      setError(null);

      const { error: verifyError } = await client.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (verifyError) {
        return { ok: false, error: translatePasswordError(verifyError.message) };
      }

      const { error: updateError } = await client.auth.updateUser({ password: newPassword });
      if (updateError) {
        return { ok: false, error: translatePasswordError(updateError.message) };
      }
      return { ok: true };
    },
    [client, session]
  );

  const refresh = useCallback(async () => {
    if (!client) return;
    const { data } = await client.auth.getSession();
    await applySession(data.session);
  }, [client, applySession]);

  const value: AuthState = {
    configured: supabaseConfigured,
    loading,
    session,
    user: session?.user ? { id: session.user.id, email: session.user.email ?? null } : null,
    identity,
    error,
    signIn,
    signOut,
    changePassword,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
}
